import { ApolloClient, createHttpLink, InMemoryCache } from '@apollo/client'
import { setContext } from '@apollo/client/link/context'
import { onError } from '@apollo/client/link/error'
import { STRAPI_USER_STORAGE_KEY } from './constants/index'
import { fetchJwt } from './utils'

const httpLink = createHttpLink({
  uri: process.env.REACT_APP_API_HOST + '/graphql'
})

const authLink = setContext((_, { headers }) => {
  // get the authentication token from local storage if it exists
  const token = fetchJwt()
  // return the headers to the context so httpLink can read them
  return {
    headers: {
      ...headers,
      authorization: token ? `Bearer ${token}` : ''
    }
  }
})

/** Messages Strapi returns when the caller's JWT is no longer usable. */
const EXPIRED_SESSION_MESSAGES = [
  'Invalid token.',
  'Invalid credentials',
  'Missing or invalid credentials'
]

const isExpiredSessionMessage = (message?: string | null) =>
  Boolean(message) && EXPIRED_SESSION_MESSAGES.some((known) => message?.includes(known))

/** Whether a failed operation is telling us the session is dead. */
const isExpiredSession = (
  graphQLErrors?: readonly { message: string }[],
  networkError?: unknown
) => {
  // Only the first error was inspected before, so an expired session that
  // arrived alongside any other error (Strapi commonly returns several) failed
  // to sign the user out and left them stuck on a dead session.
  if (graphQLErrors?.some((error) => isExpiredSessionMessage(error?.message))) return true

  // Strapi answers some rejected tokens with a 401 rather than a GraphQL error,
  // which never reaches `response.errors` at all — that route used to leave the
  // user on a session every request would refuse.
  const asNetworkError = networkError as
    | { statusCode?: number; result?: { errors?: { message: string }[] } }
    | undefined

  if (asNetworkError?.statusCode === 401) return true

  return Boolean(
    asNetworkError?.result?.errors?.some((error) => isExpiredSessionMessage(error?.message))
  )
}

let signingOut = false

const errorLink = onError(({ graphQLErrors, networkError }) => {
  if (signingOut || !isExpiredSession(graphQLErrors, networkError)) return

  // A dashboard fires many queries at once, so several can fail on the same
  // dead token. Redirect once.
  signingOut = true

  try {
    window.localStorage.removeItem(STRAPI_USER_STORAGE_KEY)
    window.sessionStorage.removeItem(STRAPI_USER_STORAGE_KEY)
  } catch {
    // Storage can be unavailable (private mode); the redirect below still applies.
  }

  window.location.replace(process.env.REACT_APP_CLIENT_HOST || '/')
})

const client = new ApolloClient({
  cache: new InMemoryCache(),
  link: authLink.concat(errorLink).concat(httpLink),
  // Keeps the Apollo devtools bridge out of production builds.
  devtools: { enabled: process.env.NODE_ENV !== 'production' }
})

export default client
